import type { ComponentType } from 'react';
import React, { useCallback } from 'react';

export interface TraceProps {
    'automation-id'?: string;
}

export interface WithTrace {
    applyTrace: (suffix?: string) => TraceProps;
}

export const useTrace =
    (tracePrefix: string) =>
    (suffix?: string): TraceProps => ({
        'automation-id': `${tracePrefix}${suffix ? `-${suffix}` : ''}`.replace(/\s/g, ''),
    });

// the `use` prefix makes react think its a hook, which makes class components grumpy. This is not necessary as its just a simple function
export const getTrace = useTrace;

export function withTrace(defaultPrefix: string) {
    return <P extends TraceProps>(WrappedComponent: ComponentType<P & WithTrace>) => {
        const tracedComponent = function TracedComponent(props: P & Partial<WithTrace>) {
            const { 'automation-id': automationId = defaultPrefix } = props;
            // In order to stop unnecessary re-renders, we will memoize this function.
            // Without this, the applyTrace prop would be a new function ref every time this is rendered.
            // Child components would consider the prop to have changed when it had not.
            // eslint-disable-next-line react-hooks/exhaustive-deps
            const applyTraceFunc = useCallback(useTrace(automationId), [automationId]);
            return (
                <WrappedComponent
                    applyTrace={applyTraceFunc}
                    automation-id={automationId}
                    {...props}
                />
            );
        };
        return tracedComponent;
    };
}
